import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Trash2, Edit, Plus, Upload } from "lucide-react";
import { api } from "@/lib/api";
import { resolveSpeakerImageUrl } from "@/lib/events";
import { initials } from "@/components/site/data";
import { RichTextEditor } from "./RichTextEditor";

interface Event {
  id: number;
  title: string;
  event_date?: string;
}

interface Speaker {
  id: number;
  name: string;
  role: string;
  about: string | null;
  description: string | null;
  image_url: string | null;
}

export function SpeakersManager() {
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [speakers, setSpeakers] = useState<Speaker[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingSpeaker, setEditingSpeaker] = useState<Speaker | null>(null);
  const [newSpeaker, setNewSpeaker] = useState({ name: "", role: "", about: "", description: "", image_url: "" });
  const [uploading, setUploading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadEvents();
  }, []);

  useEffect(() => {
    if (selectedEventId) {
      loadSpeakers(selectedEventId);
    }
  }, [selectedEventId]);

  const loadEvents = async () => {
    try {
      const response = await api.get<Event[]>("/api/events");
      if (response.success && response.data) {
        setEvents(response.data);
        if (response.data.length > 0) {
          setSelectedEventId(response.data[0].id);
        }
      }
    } catch {
      alert("Failed to load events");
    }
  };

  const loadSpeakers = async (eventId: number) => {
    try {
      const response = await api.get<Speaker[]>(`/api/speakers/event/${eventId}`);
      if (response.success && response.data) {
        setSpeakers(response.data);
      }
    } catch {
      alert("Failed to load speakers");
    }
  };

  const handleAdd = async () => {
    if (!newSpeaker.name || !newSpeaker.role) {
      alert("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post<Speaker[]>("/api/speakers", {
        ...newSpeaker,
        event_id: selectedEventId ?? undefined,
      });
      if (response.success && response.data) {
        setSpeakers(response.data);
        setNewSpeaker({ name: "", role: "", about: "", description: "", image_url: "" });
        setIsDialogOpen(false);
        alert("Speaker added successfully");
      }
    } catch (error) {
      alert("Failed to add speaker");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingSpeaker) return;

    setLoading(true);
    try {
      const response = await api.put<Speaker>(`/api/speakers/${editingSpeaker.id}`, editingSpeaker);
      if (response.success && response.data) {
        setSpeakers(speakers.map(s => s.id === editingSpeaker.id ? response.data! : s));
        setEditingSpeaker(null);
        setIsDialogOpen(false);
        alert("Speaker updated successfully");
      }
    } catch (error) {
      alert("Failed to update speaker");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Are you sure you want to delete this speaker?")) return;

    setLoading(true);
    try {
      const response = await api.delete(`/api/speakers/${id}`);
      if (response.success) {
        setSpeakers(speakers.filter(s => s.id !== id));
        alert("Speaker deleted successfully");
      }
    } catch (error) {
      alert("Failed to delete speaker");
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(`${import.meta.env.VITE_API_URL || "http://localhost:3001"}/api/upload`, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (result.success && result.data?.imageUrl) {
        if (editingSpeaker) {
          setEditingSpeaker({ ...editingSpeaker, image_url: result.data.imageUrl });
        } else {
          setNewSpeaker({ ...newSpeaker, image_url: result.data.imageUrl });
        }
      } else {
        alert('Failed to upload image');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="font-serif text-3xl text-primary mb-2">Speakers</h1>
          <p className="text-muted-foreground">Manage event speakers and their roles</p>
        </div>
      </div>

      {/* Event selector */}
      <Card className="p-6">
        <div>
          <Label htmlFor="speakers-event">Select Event</Label>
          <Select
            value={selectedEventId?.toString() || ""}
            onValueChange={(value) => setSelectedEventId(parseInt(value))}
          >
            <SelectTrigger id="speakers-event" className="mt-1">
              <SelectValue placeholder="Select an event" />
            </SelectTrigger>
            <SelectContent>
              {events.map((event) => (
                <SelectItem key={event.id} value={event.id.toString()}>
                  {event.title}
                  {event.event_date && ` (${event.event_date})`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {selectedEventId && (
      <>
      <div className="flex justify-end">
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={() => {
                setEditingSpeaker(null);
                setNewSpeaker({ name: "", role: "", about: "", description: "", image_url: "" });
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Speaker
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[80vh] flex flex-col">
            <DialogHeader>
              <DialogTitle>{editingSpeaker ? "Edit Speaker" : "Add New Speaker"}</DialogTitle>
              <DialogDescription>
                {editingSpeaker ? "Update the speaker details below." : "Add a new speaker to the event."}
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto space-y-4">
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={editingSpeaker ? editingSpeaker.name : newSpeaker.name}
                  onChange={(e) =>
                    editingSpeaker
                      ? setEditingSpeaker({ ...editingSpeaker, name: e.target.value })
                      : setNewSpeaker({ ...newSpeaker, name: e.target.value })
                  }
                  placeholder="Enter speaker name"
                />
              </div>
              <div>
                <Label htmlFor="role">Role</Label>
                <Input
                  id="role"
                  value={editingSpeaker ? editingSpeaker.role : newSpeaker.role}
                  onChange={(e) =>
                    editingSpeaker
                      ? setEditingSpeaker({ ...editingSpeaker, role: e.target.value })
                      : setNewSpeaker({ ...newSpeaker, role: e.target.value })
                  }
                  placeholder="Enter speaker role"
                />
              </div>
              <div>
                <Label htmlFor="about">About (Brief)</Label>
                <Input
                  id="about"
                  value={editingSpeaker ? editingSpeaker.about || "" : newSpeaker.about}
                  onChange={(e) =>
                    editingSpeaker
                      ? setEditingSpeaker({ ...editingSpeaker, about: e.target.value })
                      : setNewSpeaker({ ...newSpeaker, about: e.target.value })
                  }
                  placeholder="Enter brief about text"
                />
              </div>
              <div>
                <Label htmlFor="description">Description (Rich Text)</Label>
                <RichTextEditor
                  content={editingSpeaker ? editingSpeaker.description || "" : newSpeaker.description}
                  onChange={(content) =>
                    editingSpeaker
                      ? setEditingSpeaker({ ...editingSpeaker, description: content })
                      : setNewSpeaker({ ...newSpeaker, description: content })
                  }
                />
              </div>
              <div>
                <Label>Speaker Image</Label>
                <div className="flex gap-2 mt-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleBrowseClick}
                    disabled={uploading}
                    className="flex-1"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploading ? "Uploading..." : "Browse Files"}
                  </Button>
                  {(editingSpeaker?.image_url || newSpeaker.image_url) && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (editingSpeaker) {
                          setEditingSpeaker({ ...editingSpeaker, image_url: "" });
                        } else {
                          setNewSpeaker({ ...newSpeaker, image_url: "" });
                        }
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                {resolveSpeakerImageUrl(editingSpeaker?.image_url || newSpeaker.image_url) && (
                  <div className="mt-4">
                    <Label>Preview</Label>
                    <div className="mt-2 rounded-lg overflow-hidden border">
                      <img
                        src={resolveSpeakerImageUrl(editingSpeaker?.image_url || newSpeaker.image_url)!}
                        alt="Speaker preview"
                        className="w-full h-32 object-cover"
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="sticky bottom-0 bg-background border-t pt-4 flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={editingSpeaker ? handleUpdate : handleAdd} disabled={loading}>
                  {loading ? "Saving..." : editingSpeaker ? "Update" : "Add"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[72px]">Image</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Role</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {speakers.map((speaker) => {
              const imageUrl = resolveSpeakerImageUrl(speaker.image_url);
              return (
                <TableRow key={speaker.id}>
                  <TableCell>
                    <div className="h-12 w-12 rounded-md overflow-hidden bg-muted border flex items-center justify-center">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={speaker.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <span className="text-xs font-medium text-muted-foreground">
                          {initials(speaker.name)}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">{speaker.name}</TableCell>
                  <TableCell>{speaker.role}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditingSpeaker(speaker);
                          setIsDialogOpen(true);
                        }}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDelete(speaker.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
      </>
      )}
    </div>
  );
}
